/**
 * How to comment, made obvious (DESIGN.md "Comments sit in parts"; Pen
 * r9LhBH, Nttnv, XlAvc): hovering a part of the post that takes a comment (the
 * hook, the insight, the objective, the reference, a shot, a slide) shows
 * Tooltip / Action at its top right: "Comment on this part" and the key C. A
 * click on the part or the tooltip, or C while it is hovered, opens the
 * composer on that part; on a phone a tap does. Each open thread pinned to a
 * part shows its number in a Comment marker at the part's top right; the
 * marker of the thread in hand (selected, or the part being commented on) is
 * lit and its part outlined in the accent.
 *
 * The documents stay as they are: a part is found by `data-comment-part`
 * ("hook", or "shot:3") where a document sets it, else by the concept
 * document's and the brief's own structure.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { MessageSquarePlus } from "lucide-react";
import { partLabel } from "../clientReviewModel";
import type { ClientCommentPart, ClientMarker } from "./useClientComments";
import "./clientReview.css";

type Found = ClientCommentPart & { element: HTMLElement };

const LABELS: Record<string, string> = {
  hook: "the hook",
  objective: "the objective",
  sources: "the sources",
  // Older threads pinned to parts the slide no longer draws.
  insight: "the insight",
  evidence: "the evidence",
  reference: "the reference",
  title: "the title",
  synopsis: "the synopsis",
};

function labelOf(part: string, key: string | null): string {
  if (part === "shot" || part === "slide") return partLabel(`${part === "shot" ? "Shot" : "Slide"} ${key ?? ""}`.trim());
  return LABELS[part] ?? part.replace(/_/g, " ");
}

function make(element: HTMLElement, part: string, key: string | null = null): Found {
  return { element, part, key, label: labelOf(part, key) };
}

/** Every part of the page under `root` that takes a comment. */
function collectParts(root: HTMLElement): Found[] {
  const out: Found[] = [];
  const seen = new Set<HTMLElement>();
  const add = (found: Found) => {
    if (seen.has(found.element)) return;
    seen.add(found.element);
    out.push(found);
  };
  root.querySelectorAll<HTMLElement>("[data-comment-part]").forEach((element) => {
    const [part, key] = (element.dataset.commentPart ?? "").split(":");
    if (part) add(make(element, part, key ?? null));
  });
  // The brief numbers its rows by id ("…-shot-2" is shot 3).
  root.querySelectorAll<HTMLElement>("article[id*='-shot-'], article[id*='-slide-']").forEach((element) => {
    const match = element.id.match(/-(shot|slide)-(\d+)$/);
    if (match) add(make(element, match[1], String(Number(match[2]) + 1)));
  });
  // The concept document: the hook's figure, the reference, the cards under Why it works.
  root.querySelectorAll<HTMLElement>("figure").forEach((element) => {
    if (element.querySelector("blockquote")) add(make(element, "hook"));
  });
  root.querySelectorAll<HTMLElement>("aside[aria-label='The reference']").forEach((element) => add(make(element, "reference")));
  root.querySelectorAll<HTMLElement>("section[aria-label='Why it works'] > div > div").forEach((element) => {
    const head = element.querySelector("p")?.textContent?.trim().toLowerCase() ?? "";
    if (head.startsWith("the insight")) add(make(element, "insight"));
    else if (head.startsWith("the objective")) add(make(element, "objective"));
    else if (head.startsWith("sources")) add(make(element, "sources"));
    else if (head.startsWith("evidence")) add(make(element, "evidence"));
  });
  return out;
}

/** The innermost commentable part under `target`, inside `root`, or null. */
export function findPart(target: Element | null, root: HTMLElement): Found | null {
  if (!target || !root.contains(target)) return null;
  let best: Found | null = null;
  for (const found of collectParts(root)) {
    if (!found.element.contains(target)) continue;
    if (!best || best.element.contains(found.element)) best = found;
  }
  return best;
}

type Box = { top: number; left: number; width: number; height: number };

const samePart = (a: { part: string; key?: string | null }, b: { part: string; key?: string | null }) =>
  a.part === b.part && (a.key ?? null) === (b.key ?? null);

export default function CommentableParts({
  enabled,
  touch = false,
  onComment,
  onHoverPart,
  markers = [],
  active = null,
  activeNumber = null,
  onMarker,
  reveal = 0,
  children,
}: {
  /** Off for a reader who cannot comment, and once comments close. */
  enabled: boolean;
  /** A phone: no hover, a tap comments. */
  touch?: boolean;
  onComment: (part: ClientCommentPart) => void;
  /** The hovered part, so the page's C key comments on it. */
  onHoverPart?: (part: ClientCommentPart | null) => void;
  /** The open threads pinned to parts on this tab and version. */
  markers?: ClientMarker[];
  /** The part in hand: the selected thread's, or the one being commented on. */
  active?: { part: string; key?: string | null } | null;
  /** The number a new comment on the active part will take, when no thread has it yet. */
  activeNumber?: number | null;
  onMarker?: (threadId: string) => void;
  /** Bumped by Show on the page: the active part scrolls into view. */
  reveal?: number;
  children: ReactNode;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [found, setFound] = useState<Found | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [boxes, setBoxes] = useState<{ part: string; key: string | null; box: Box }[]>([]);
  const current = useRef<Found | null>(null);

  const boxOf = useCallback((element: HTMLElement): Box | null => {
    const root = rootRef.current;
    if (!root) return null;
    const outer = root.getBoundingClientRect();
    const inner = element.getBoundingClientRect();
    return { top: inner.top - outer.top, left: inner.left - outer.left, width: inner.width, height: inner.height };
  }, []);

  const set = useCallback(
    (next: Found | null) => {
      if (current.current?.element !== next?.element) {
        current.current = next;
        setFound(next);
        onHoverPart?.(next ? { part: next.part, key: next.key, label: next.label } : null);
      }
      setBox(next ? boxOf(next.element) : null);
    },
    [boxOf, onHoverPart],
  );

  // Where every marked or active part sits, measured again whenever the page moves under it.
  const wanted = [...markers.map((marker) => ({ part: marker.part, key: marker.key })), ...(active ? [active] : [])];
  const wantedKey = wanted.map((entry) => `${entry.part}:${entry.key ?? ""}`).join("|");
  const measureAll = useCallback(() => {
    const root = rootRef.current;
    if (!root || !wantedKey) {
      setBoxes((list) => (list.length ? [] : list));
      return;
    }
    const parts = collectParts(root);
    const next: { part: string; key: string | null; box: Box }[] = [];
    for (const entry of wantedKey.split("|")) {
      const [part, key] = entry.split(":");
      const hit = parts.find((candidate) => samePart(candidate, { part, key: key || null }));
      const measured = hit ? boxOf(hit.element) : null;
      if (measured) next.push({ part, key: key || null, box: measured });
    }
    setBoxes(next);
  }, [boxOf, wantedKey]);

  useLayoutEffect(() => {
    measureAll();
  }, [measureAll]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      measureAll();
      if (current.current) setBox(boxOf(current.current.element));
    });
    observer.observe(root);
    if (found) observer.observe(found.element);
    return () => observer.disconnect();
  }, [found, measureAll, boxOf]);

  // Tab walks the parts: each takes focus while commenting is on, and a
  // focused part is the one C comments on (Pen m2w7Id).
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled || touch) return;
    const parts = collectParts(root);
    const touched: HTMLElement[] = [];
    for (const { element, label } of parts) {
      if (element.hasAttribute("tabindex")) continue;
      element.setAttribute("tabindex", "0");
      element.setAttribute("data-co-part-focus", "");
      if (!element.getAttribute("aria-label")) element.setAttribute("aria-label", `${label}, press C to comment`);
      touched.push(element);
    }
    return () => {
      for (const element of touched) {
        element.removeAttribute("tabindex");
        element.removeAttribute("data-co-part-focus");
        if (element.getAttribute("aria-label")?.endsWith("press C to comment")) element.removeAttribute("aria-label");
      }
    };
  });

  // Show on the page: bring the part in hand into view.
  useEffect(() => {
    if (!reveal || !active) return;
    const root = rootRef.current;
    const hit = root ? collectParts(root).find((candidate) => samePart(candidate, active)) : null;
    hit?.element.scrollIntoView({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    // Only a new request scrolls, never a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal]);

  const boxFor = (part: { part: string; key?: string | null }) => boxes.find((entry) => samePart(entry, part))?.box ?? null;
  const activeBox = active ? boxFor(active) : null;
  const activeMarked = active ? markers.some((marker) => samePart(marker, active)) : false;

  return (
    <div
      ref={rootRef}
      className="co-cr-parts"
      data-commentable={enabled || undefined}
      onPointerOver={(event) => {
        if (!enabled || touch || event.pointerType === "touch" || !rootRef.current) return;
        // Moving onto the tooltip keeps the part it belongs to.
        if ((event.target as Element).closest(".co-cr-part-tip, .co-cr-marker")) return;
        set(findPart(event.target as Element, rootRef.current));
      }}
      onPointerLeave={() => {
        if (!rootRef.current?.contains(document.activeElement)) set(null);
      }}
      onFocus={(event) => {
        if (!enabled || touch || !rootRef.current) return;
        const target = event.target as HTMLElement;
        if (!target.hasAttribute("data-co-part-focus")) return;
        set(findPart(target, rootRef.current));
      }}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) set(null);
      }}
      onKeyDown={(event) => {
        if (!enabled || !rootRef.current) return;
        const target = event.target as HTMLElement;
        if (event.key !== "Enter" || !target.hasAttribute("data-co-part-focus")) return;
        const part = findPart(target, rootRef.current);
        if (!part) return;
        event.preventDefault();
        onComment({ part: part.part, key: part.key, label: part.label });
      }}
      onClick={(event) => {
        if (!enabled) return;
        const target = event.target as HTMLElement;
        if (target.closest("button, a, input, textarea, video, [role='button']")) return;
        const part = rootRef.current ? findPart(target, rootRef.current) : null;
        if (part) onComment({ part: part.part, key: part.key, label: part.label });
      }}
    >
      {children}
      {activeBox ? (
        <div
          className="co-cr-part-active"
          aria-hidden="true"
          style={{ top: activeBox.top, left: activeBox.left, width: activeBox.width, height: activeBox.height }}
        />
      ) : null}
      {markers.map((marker) => {
        const at = boxFor(marker);
        if (!at) return null;
        const lit = !!active && samePart(marker, active);
        return (
          <button
            key={marker.threadId}
            type="button"
            className="co-cr-marker"
            data-active={lit || undefined}
            style={{ top: at.top - 10, left: at.left + at.width - 14 }}
            aria-label={`Comment ${marker.number}, on ${labelOf(marker.part, marker.key)}`}
            onClick={() => onMarker?.(marker.threadId)}
          >
            {marker.number}
          </button>
        );
      })}
      {active && activeBox && !activeMarked && activeNumber ? (
        <span
          className="co-cr-marker"
          data-active
          aria-hidden="true"
          style={{ top: activeBox.top - 10, left: activeBox.left + activeBox.width - 14 }}
        >
          {activeNumber}
        </span>
      ) : null}
      {enabled && !touch && found && box ? (
        <button
          type="button"
          className="co-cr-part-tip co-glass-chrome"
          style={{ top: box.top - 14, left: box.left + box.width - 10 }}
          onClick={() => onComment({ part: found.part, key: found.key, label: found.label })}
        >
          <MessageSquarePlus strokeWidth={1.8} aria-hidden="true" />
          Comment on this part
          <kbd>C</kbd>
        </button>
      ) : null}
    </div>
  );
}
